import React from "react";
import {
  Bell, Tag, Check, UserMinus, ArrowRight, SquareUser,
  CornerUpLeft, StickyNote, Zap, Star, Smile, Paperclip, Package, Hammer, MapPin, Link2, Mic,
} from "lucide-react";
import { ChannelIcon } from "./iconesCanais";
import { AvatarIniciais } from "./ListaConversas";
import { CORES, MENSAGENS, ATENDENTE, CLIENTE } from "./dadosMockup";

// Botoes quadrados do cabecalho real (cliente vinculado):
// Tags, Finalizar, Desassociar, Transferir, Editar Cliente.
const ACOES = [
  { Icone: Check, cor: "#10B981" },
  { Icone: UserMinus, cor: "#64748B" },
  { Icone: ArrowRight, cor: "#8B5CF6" },
  { Icone: SquareUser, cor: "#0EA5E9" },
];

function CabecalhoChat() {
  return (
    <div
      className="flex items-center justify-between h-16 px-6 shrink-0"
      style={{ background: CORES.card, borderBottom: `1px solid ${CORES.borda}`, boxShadow: "0 10px 15px -3px rgba(0,0,0,0.25)" }}
    >
      {/* Sistema real: sem avatar no cabecalho — so nome + canal + status */}
      <div>
        <div className="text-base font-bold" style={{ color: CORES.texto }}>{CLIENTE.nome}</div>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: CORES.textoMuted }}>
          <ChannelIcon canal="whatsapp" className="w-3 h-3" />
          <span>WhatsApp</span>
          <span>•</span>
          <span className="font-medium text-green-600">Online agora</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center">
          <Bell className="w-4 h-4 text-blue-600" />
        </div>
        {/* Botao de tags expandido: primeira tag + "+N", como no sistema */}
        <div className="h-9 pl-2.5 pr-2 rounded-lg flex items-center gap-1.5 text-white shadow-lg" style={{ background: "#F59E0B" }}>
          <Tag className="w-4 h-4" />
          <span className="text-xs font-semibold">Vendas</span>
          <span className="text-[10px] font-bold px-1.5 rounded-full bg-white/25">+1</span>
        </div>
        {ACOES.map(({ Icone, cor }, i) => (
          <div key={i} className="w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-lg" style={{ background: cor }}>
            <Icone className="w-4 h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Rabinho do balao, mesma geometria do CSS do sistema (10x16, canto superior).
function Rabinho({ lado, cor, borda }) {
  const recebida = lado === "esquerda";
  return (
    <svg
      viewBox="0 0 10 16"
      className="absolute"
      style={{ top: -1, width: 10, height: 16, [recebida ? "left" : "right"]: -8 }}
    >
      <path d={recebida ? "M10 0 L10 16 L0 0 Z" : "M0 0 L0 16 L10 0 Z"} fill={cor} />
      <path d={recebida ? "M10 0 L0 0 L10 16" : "M0 0 L10 0 L0 16"} fill="none" stroke={borda} strokeWidth="1" />
    </svg>
  );
}

function Mensagem({ msg }) {
  const recebida = msg.de === "cliente";
  const nome = recebida ? CLIENTE.nome : ATENDENTE;
  const bg = recebida ? CORES.balaoRecebido : CORES.balaoEnviado;
  const borda = recebida ? CORES.balaoRecebidoBorda : CORES.balaoEnviado;

  return (
    <div className={`flex flex-col ${recebida ? "items-start" : "items-end"}`}>
      {/* Cabecalho acima do balao: avatar, nome e horario */}
      <div className={`flex items-center gap-2 mb-1.5 ${recebida ? "" : "flex-row-reverse"}`}>
        {recebida ? (
          <AvatarIniciais nome={nome} tamanho={28} className="opacity-80" />
        ) : (
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ background: "#3b82f6" }}>
            {ATENDENTE[0]}
          </div>
        )}
        <span className="text-xs font-semibold" style={{ color: CORES.texto }}>{nome}</span>
        <span className="text-[11px]" style={{ color: CORES.textoMuted }}>{msg.hora}</span>
      </div>
      <div
        className="relative"
        style={{
          background: bg,
          border: `1px solid ${borda}`,
          borderRadius: 18,
          [recebida ? "borderTopLeftRadius" : "borderTopRightRadius"]: 4,
          padding: "8px 12px",
          maxWidth: "75%",
          color: CORES.balaoTexto,
          fontSize: 13.5,
          lineHeight: 1.4,
          [recebida ? "marginLeft" : "marginRight"]: 8,
          boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
        }}
      >
        <Rabinho lado={recebida ? "esquerda" : "direita"} cor={bg} borda={borda} />
        {msg.texto}
        {msg.link && (
          <div className="mt-1 underline" style={{ color: "#53BDEB" }}>{msg.link}</div>
        )}
      </div>
    </div>
  );
}

function Composer() {
  const ferramenta = "w-8 h-8 rounded-xl flex items-center justify-center";
  const corFerramenta = { color: "rgba(154,157,165,0.7)" };
  return (
    <div className="mx-3 mb-3 p-3 rounded-3xl shrink-0" style={{ background: CORES.card, border: `1px solid ${CORES.borda}` }}>
      {/* Abas: Mensagem (ativa), Nota Particular, Respostas Rápidas, Favoritas */}
      <div className="inline-flex items-center gap-1 h-9 p-1 rounded-lg mb-2" style={{ background: "rgba(23,23,23,0.3)" }}>
        <div className="h-7 px-3 rounded-md flex items-center gap-1.5 text-xs font-medium" style={{ background: "rgba(91,141,239,0.15)", color: CORES.primary }}>
          <CornerUpLeft className="w-3.5 h-3.5" />
          Mensagem
        </div>
        {[StickyNote, Zap, Star].map((Icone, i) => (
          <div key={i} className="w-7 h-7 rounded-md flex items-center justify-center" style={{ color: CORES.textoMuted }}>
            <Icone className="w-3.5 h-3.5" />
          </div>
        ))}
      </div>

      <div
        className="relative rounded-xl p-3 text-sm h-[58px]"
        style={{ background: CORES.fundo, border: "1px solid rgba(46,46,46,0.5)", color: CORES.textoMuted }}
      >
        Escreva sua mensagem...
        <div className="absolute right-1.5 bottom-1.5 w-8 h-8 rounded-full flex items-center justify-center" style={{ color: CORES.textoMuted }}>
          <Smile className="w-4 h-4" />
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-0.5">
          <div className={ferramenta} style={corFerramenta}><Paperclip className="w-4 h-4" /></div>
          <div className="w-px h-5 mx-1" style={{ background: CORES.borda }} />
          <div className={ferramenta} style={corFerramenta}><Package className="w-4 h-4" /></div>
          <div className={ferramenta} style={corFerramenta}><Hammer className="w-4 h-4" /></div>
          <div className={ferramenta} style={corFerramenta}><MapPin className="w-4 h-4" /></div>
          <div className={ferramenta} style={corFerramenta}><Link2 className="w-4 h-4" /></div>
        </div>
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: CORES.primary }}>
          <Mic className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );
}

export default function ChatAtivo() {
  return (
    <div className="flex flex-col h-full min-w-0" style={{ background: "#121212" }}>
      <CabecalhoChat />
      <div className="flex-1 min-h-0 overflow-hidden p-4 flex flex-col gap-4">
        {/* Separador de data */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px" style={{ background: CORES.borda }} />
          <span className="text-xs font-medium uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: CORES.textoMuted, background: "rgba(23,23,23,0.5)" }}>
            Hoje
          </span>
          <div className="flex-1 h-px" style={{ background: CORES.borda }} />
        </div>
        {MENSAGENS.map((m, i) => (
          <Mensagem key={i} msg={m} />
        ))}
      </div>
      <Composer />
    </div>
  );
}
